import type { Column, Header } from '@tanstack/vue-table';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { ColumnWindowColumn, DataTableColumnPinGroup } from './data-table-column-window';
import { computed, toValue } from 'vue';
import { computeRenderedCenterColumns, DEFAULT_COLUMN_WINDOW_OVERSCAN_PX } from './data-table-column-window';

export interface UseDataTableColumnWindowOptions<T> {
	/** Window center columns only while this is true (virtualized + widths frozen). */
	enabled: MaybeRefOrGetter<boolean>;
	/** Flat, ordered visible leaf headers — the same list that builds the grid tracks. */
	visibleColumns: MaybeRefOrGetter<Header<T, unknown>[]>;
	/** Frozen per-column widths so the window aligns with the rendered CSS grid tracks. */
	resolvedColumnWidths: MaybeRefOrGetter<Record<string, number>>;
	getColumnPinGroup: (column: Column<T>) => DataTableColumnPinGroup;
	/** Horizontal scroll offset of the table root, in pixels. */
	scrollLeft: MaybeRefOrGetter<number>;
	/** Visible width of the table root, in pixels. */
	viewportWidth: MaybeRefOrGetter<number>;
	overscanPx?: number;
}

export interface UseDataTableColumnWindowReturn {
	/** True when center-column windowing is actually applied. */
	active: ComputedRef<boolean>;
	/** Whether a cell for the given column should be rendered in the body rows. */
	isColumnRendered: (columnId: string) => boolean;
	/** 1-based CSS grid track index for the column, or undefined when unknown. */
	getColumnGridIndex: (columnId: string) => number | undefined;
}

function areSameIds(a: Set<string>, b: Set<string>): boolean {
	if (a.size !== b.size) return false;

	for (const id of a) {
		if (!b.has(id)) return false;
	}

	return true;
}

/**
 * Horizontal (center-column) virtualization for the shared data table.
 *
 * The table renders a single CSS grid whose track list covers every visible
 * column, so pinned offsets, resize handles, header measurement, and scroll
 * width all stay correct. This composable decides which center columns actually
 * need body cells for the current horizontal scroll position; offscreen center
 * columns keep their grid track (as an implicit spacer) but skip their cell.
 *
 * Pinned (left/right) columns are always rendered so their sticky behavior is
 * untouched.
 */
export function useDataTableColumnWindow<T>(options: UseDataTableColumnWindowOptions<T>): UseDataTableColumnWindowReturn {
	const overscanPx = options.overscanPx ?? DEFAULT_COLUMN_WINDOW_OVERSCAN_PX;

	const gridIndexById = computed(() => {
		const map = new Map<string, number>();
		for (const [index, header] of toValue(options.visibleColumns).entries()) map.set(header.id, index + 1);
		return map;
	});

	const pinGroupById = computed(() => {
		const map = new Map<string, DataTableColumnPinGroup>();

		for (const header of toValue(options.visibleColumns)) {
			map.set(header.id, options.getColumnPinGroup(header.column));
		}

		return map;
	});

	const windowColumns = computed<ColumnWindowColumn[]>(() => {
		const widths = toValue(options.resolvedColumnWidths);
		return toValue(options.visibleColumns).map((header) => ({
			id: header.id,
			pinGroup: options.getColumnPinGroup(header.column),
			width: widths[header.id] ?? header.getSize(),
		}));
	});

	const active = computed(() => toValue(options.enabled) && toValue(options.viewportWidth) > 0);

	// Cache the rendered id set so identical windows keep a stable reference:
	// scrolling within a column then yields the same computed value and skips
	// re-rendering every body row (Vue short-circuits equal computed results).
	let cachedIds = new Set<string>();
	const renderedCenterIds = computed(() => {
		if (!active.value) return cachedIds;

		const next = computeRenderedCenterColumns(
			windowColumns.value,
			toValue(options.scrollLeft),
			toValue(options.viewportWidth),
			overscanPx,
		);

		if (areSameIds(next, cachedIds)) return cachedIds;

		cachedIds = next;
		return cachedIds;
	});

	function isColumnRendered(columnId: string): boolean {
		if (!active.value) return true;
		if (pinGroupById.value.get(columnId) !== 'center') return true;
		return renderedCenterIds.value.has(columnId);
	}

	function getColumnGridIndex(columnId: string): number | undefined {
		return gridIndexById.value.get(columnId);
	}

	return { active, isColumnRendered, getColumnGridIndex };
}
